import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@store/authStore';

// API base URL from environment
// The Worker API includes /api/v1 in its routes
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Send cookies for Cloudflare Access
});

// Request interceptor - add active tenant context
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const tenantId = useAuthStore.getState().tenant?.tenantId || localStorage.getItem('bizops-tenant');
    if (tenantId) {
      config.headers['X-Tenant-Id'] = tenantId;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle Cloudflare Access session state
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<{ code?: string }>) => {
    if (error.response?.status === 401) {
      window.location.reload();
    }

    if (error.response?.status === 403 && error.response.data?.code === 'not_provisioned') {
      useAuthStore.getState().setNotProvisioned(true);
    }

    return Promise.reject(error);
  }
);

// Generic API response type
export interface ApiResponse<T> {
  data: T;
  message?: string;
}

// Paginated response type
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export default apiClient;
